import React, { useState } from 'react';
import {
  Typography,
  Stack,
  Divider,
  Alert,
  Button,
  IconButton,
  styled,
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AttachFile as AttachAllIcon,
} from '@mui/icons-material';
import DocumentCard from './DocumentCard';

interface DocumentSearchResult {
  document_node_id: string;
  title: string;
  content_preview?: string;
  relevance_score: number;
  document_type?: string;
  document_source?: string;
  document_url?: string;
  metadata?: any;
}

interface DocumentSearchResultsProps {
  taskTitle?: string;
  searchQuery?: string;
  results: DocumentSearchResult[];
  totalCount: number;
  onAttachDocument?: (documentId: string) => void;
  onViewDocument?: (url: string) => void;
  onAttachAll?: () => void;
  maxVisible?: number;
}

const SearchHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
}));

const ResultsContainer = styled('div')(({ theme }) => ({
  maxHeight: '400px',
  overflowY: 'auto',
  padding: theme.spacing(1),
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
}));

const DocumentSearchResults: React.FC<DocumentSearchResultsProps> = ({
  taskTitle,
  searchQuery,
  results,
  totalCount,
  onAttachDocument,
  onViewDocument,
  onAttachAll,
  maxVisible = 5,
}) => {
  const [showAll, setShowAll] = useState(false);

  if (results.length === 0) {
    return (
      <div style={{ padding: 16 }}>
        <SearchHeader>
          <SearchIcon color="action" />
          <Typography variant="h6" color="text.secondary">
            No Documents Found
          </Typography>
        </SearchHeader>

        <Alert severity="info" style={{ marginTop: 16 }}>
          <Typography variant="body2">
            {searchQuery ? `No documents found for "${searchQuery}"` : 'No documents found'}
            {taskTitle && ` related to task "${taskTitle}"`}
          </Typography>
          <Typography variant="body2" style={{ marginTop: 8, fontSize: '0.875rem' }}>
            Try:
            <br />• Adding more specific keywords
            <br />• Checking if documents exist in your knowledge base
            <br />• Using different search terms
          </Typography>
        </Alert>
      </div>
    );
  }

  const visibleResults = showAll ? results : results.slice(0, maxVisible);
  const hasMore = results.length > maxVisible;
  const highRelevanceCount = results.filter(r => r.relevance_score >= 0.8).length;

  return (
    <div style={{ padding: 16 }}>
      <SearchHeader>
        <SearchIcon color="primary" />
        <div style={{ flexGrow: 1 }}>
          <Typography variant="h6">
            Found {totalCount} Relevant Document{totalCount > 1 ? 's' : ''}
          </Typography>
          {taskTitle && (
            <Typography variant="body2" color="text.secondary">
              for task "{taskTitle}"
            </Typography>
          )}
          {searchQuery && (
            <Typography variant="body2" color="text.secondary">
              searched: "{searchQuery}"
            </Typography>
          )}
        </div>

        {onAttachAll && highRelevanceCount > 1 && (
          <Button
            startIcon={<AttachAllIcon />}
            onClick={onAttachAll}
            size="small"
            variant="outlined"
            color="primary"
          >
            Attach All ({highRelevanceCount})
          </Button>
        )}

        {hasMore && (
          <IconButton
            onClick={() => setShowAll(!showAll)}
            size="small"
            color="primary"
          >
            {showAll ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}
      </SearchHeader>

      <ResultsContainer>
        <Stack spacing={2}>
          {visibleResults.map((result, index) => (
            <DocumentCard
              key={result.document_node_id || index}
              document={{
                title: result.title,
                type: result.document_type || 'Document',
                preview: result.content_preview,
                relevance: result.relevance_score,
                source: result.document_source || 'Unknown',
                url: result.document_url,
                document_node_id: result.document_node_id,
                attachment_reason: `Found with ${Math.round(result.relevance_score * 100)}% relevance`,
              }}
              onAttach={onAttachDocument}
              onView={onViewDocument}
              compact={visibleResults.length > 3}
            />
          ))}

          {hasMore && !showAll && (
            <>
              <Divider />
              <div style={{ textAlign: 'center', paddingTop: 8, paddingBottom: 8 }}>
                <Button
                  onClick={() => setShowAll(true)}
                  startIcon={<ExpandMoreIcon />}
                  color="primary"
                  variant="text"
                >
                  Show {results.length - maxVisible} more documents
                </Button>
              </div>
            </>
          )}
        </Stack>
      </ResultsContainer>

      <div style={{ marginTop: 16, padding: 8, backgroundColor: '#fff', borderRadius: 4 }}>
        <Typography variant="caption" color="text.secondary">
          Use the attach button to link documents to your task for easy access
        </Typography>
      </div>
    </div>
  );
};

export default DocumentSearchResults;
